"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { debounce } from "lodash";

import styles from "@/components/common/QuizBanner/QuizBanner.module.scss";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import Slider from "@/lib/react-slick";
import { cn } from "@/utils/general";

const HEIGHT = 260;
const waves = [1, 20, 40, 60]; // y values for the waves

interface Cause {
  id: string;
  name: string;
  value: number;
}

interface WaveProps {
  y: number;
  index: number;
  factor: number;
  isActive: boolean;
}

interface WavesSliderProps {
  causes: Cause[];
  mode: string;
}

function getFactor(value: number, values: Cause[]): number {
  const max = Math.max(...values.map((v) => v.value));

  return value / max;
}

const getShape = (y: number, factor: number): string => {
  const computedY = HEIGHT + y - Math.abs(factor) * (HEIGHT - 1);
  return `M0 ${HEIGHT}C106 ${HEIGHT} 89 ${computedY} 150 ${computedY}C211 ${computedY} 193 ${HEIGHT} 300 ${HEIGHT}`;
};

const Wave: React.FC<WaveProps> = ({ y, index, factor, isActive }) => {
  const waveRef = useRef(null);
  useGSAP(() => {
    if (waveRef.current && isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y + 10, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y, factor) },
          ease: "circ.out",
          delay: 1,
        }
      );
    }
    if (waveRef.current && !isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y + 10, factor) },
          ease: "circ.out",
          delay: 1,
        }
      );
    }
  }, [isActive]);

  return (
    <path
      shapeRendering="geometricPrecision"
      d={getShape(y + 10, factor)}
      strokeLinejoin="round"
      ref={waveRef}
      stroke="#fff"
      strokeWidth="1"
      opacity={1 - index * 0.25}
    />
  );
};

export default function WavesSlider({ causes, mode }: WavesSliderProps) {
  const wrapperRef = useRef(null);
  const sliderRef = useRef(null);
  const causeTextRef = useRef(null);
  const dotRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useGSAP(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapperRef.current,
        start: "top center",
        end: "center center",
        toggleActions: "play none none reset",
        pinnedContainer: "#growth-pin-wrapper",
        invalidateOnRefresh: true,
      },
    });
    tl.fromTo(wrapperRef.current, { opacity: 0 }, { opacity: 1, duration: 1 });
  });

  useGSAP(() => {
    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 3,
      scrollTrigger: {
        trigger: wrapperRef.current,
        start: "30% center",
        toggleActions: "play none none reset",
        pinnedContainer: "#growth-pin-wrapper",
        invalidateOnRefresh: true,
      },
    });

    tl.fromTo(
      causeTextRef.current,
      {
        opacity: 1,
      },
      {
        opacity: 0,
        duration: 0.3,
      }
    )
      .add(() => {
        sliderRef.current.slickNext();
      }, 0.3)
      .fromTo(
        causeTextRef.current,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.3,
          delay: 0.5,
        },
        1
      );
  });

  const setDotAndTextPos = useCallback(() => {
    if (!causeTextRef.current || !dotRef.current || !sliderRef.current) return;
    const sliderHeight = sliderRef.current.innerSlider.list.offsetHeight;
    const yBottomPos =
      sliderHeight * getFactor(causes[activeIndex].value, causes) - causes[activeIndex].value;

    gsap.set(causeTextRef.current, {
      y: -Math.abs(yBottomPos) - 20,
    });

    gsap.to(dotRef.current, {
      ease: "circ.out",
      duration: 1,
      y: -Math.abs(yBottomPos) - dotRef.current.offsetHeight / 2,
    });
  }, [activeIndex, causes]);

  useGSAP(
    () => {
      setDotAndTextPos();
    },
    { dependencies: [activeIndex, causes] }
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const debouncedSetDotAndTextPos = debounce(() => setDotAndTextPos(), 500);
    window.addEventListener("resize", debouncedSetDotAndTextPos);
    return () => window.removeEventListener("resize", debouncedSetDotAndTextPos);
  }, [setDotAndTextPos]);

  const settings = {
    dots: false,
    infinite: true,
    slidesToScroll: 1,
    arrows: false,
    speed: 1000,
    slidesToShow: 1,
    centerMode: true,
    variableWidth: true,
    autoplay: false,
    touchMove: false,
    beforeChange: (_: number, index: number) => {
      setActiveIndex(index);
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.7,
        },
      },
    ],
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(styles.slideWrapper, "relative z-[2] w-full h-full will-change-opacity")}
    >
      <div
        className={cn(
          styles.divider,
          "pointer-events-none absolute -top-[30px] bottom-0 w-[2px] -ml-[1px] left-1/2 z-[1]"
        )}
      />
      <div className={cn(styles.slideTrack, "relative z-[2] lg:top-1/2 lg:-translate-y-1/2")}>
        <div
          ref={causeTextRef}
          className={cn(
            styles.causeText,
            styles[mode],
            "absolute bottom-0 left-1/2 -translate-x-1/2 z-[4]"
          )}
        >
          <TextElementNova
            text={causes[activeIndex].name}
            className="Caption-10-Dia-Uppercase text-nova-base-white"
          />
        </div>
        <Slider {...settings} ref={sliderRef}>
          {causes.map((cause: Cause, i: number) => {
            const factor = getFactor(cause.value, causes);
            return (
              <div key={i} className="relative z-3">
                <svg
                  width="100%"
                  viewBox="0 0 300 261"
                  fill="none"
                  style={{ transform: "translate3d(0px, 0px, 0px)" }}
                >
                  {waves.map((y: number, ii: number) => (
                    <Wave key={ii} isActive={i === activeIndex} y={y} index={ii} factor={factor} />
                  ))}
                </svg>
              </div>
            );
          })}
        </Slider>
        <div
          className={cn(
            styles.dots,
            "absolute left-1/2 z-[5] -translate-x-1/2 -translate-y-[2px] md:translate-y-[1px] lg:translate-y-[2px]"
          )}
        >
          <div
            ref={dotRef}
            className={cn(
              styles.dot,
              "absolute rounded-full bg-[#ffc942] bottom-0 -translate-x-1/2 w-[6px] h-[6px] md:w-[8px] md:h-[8px] lg:w-[10px] lg:h-[10px]"
            )}
          />
          <div
            className={cn(
              styles.dot,
              styles.static,
              "absolute rounded-full bg-[#ffc942] bottom-0 -translate-x-1/2 w-[6px] h-[6px] md:w-[8px] md:h-[8px] lg:w-[10px] lg:h-[10px]"
            )}
          />
        </div>
      </div>
    </div>
  );
}
